/* eslint-disable new-cap */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Component rendering the live clock in format "Q1 00:00"
 * The time updated every second
 *
 * @param {object} props
 * @param {number} props.periodSeconds Seconds elapsed of the period. This is the value the ticker starts incrementing from
 * @param {number} props.periodNumber Number of the period as coming from the API (can be more than 4)
 * @param {number} props.periodNumberSanitized Sanitized number of the period, 1-4 for Quarters, 1+ for Additional TIme
 * @param {boolean} props.isCountdown If true, the time will be counting down instead of up. Default FALSE
 * @returns {JSXElement} snan element with live clock
 */
const MatchCardClock = ({
    periodSeconds,
    periodNumber,
    periodNumberSanitized,
    isCountdown = false
}) => {
    const timeoutRef = useRef();
    const [durationSeconds, setDurationSeconds] = useState(periodSeconds);

    const tick = () => {
        if (isCountdown) {
            setDurationSeconds((seconds) => seconds - 1);
        } else {
            setDurationSeconds((seconds) => seconds + 1);
        }

        timeoutRef.current = setTimeout(
            () => tick(),
            PULSE.app.common.CONSTANTS.TIME.ONE_SECOND_IN_MS
        );
    };

    useEffect(() => {
        clearTimeout(timeoutRef.current);
        tick();
    }, [periodSeconds]);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    const translationLabel = useMemo(() => {
        if (periodNumber > PULSE.app.common.CONSTANTS.MAX_PERIOD_NUMBER) {
            return 'label.mc.clock.extraTime.noSpan';
        }

        return 'label.mc.clock.quarterTime.noSpan';
    }, [periodNumber]);

    return (
        <span className="tabular-nums">
            {PULSE.I18N.lookup(translationLabel, {
                number: periodNumberSanitized,
                time: moment
                    .duration(durationSeconds, 'seconds')
                    .format('mm:ss', { trim: false })
            })}
        </span>
    );
};

MatchCardClock.propTypes = {
    periodSeconds: PropTypes.number,
    periodNumber: PropTypes.number,
    periodNumberSanitized: PropTypes.number,
    isCountdown: PropTypes.bool
};

export default MatchCardClock;
